import { useEffect, useState } from 'react';
import {
  Create,
  SaveContextProvider,
  TabbedForm,
  Title,
  useDataProvider,
  useNotify,
} from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CustomTabbedForm, useGetCurrentStep } from '@pumpkincare/shared';
import { useCheckEmail } from '@pumpkincare/users';

import { stripeErrorStatus } from '../../../../../constants/stripe-error-status';
import RoutePaths from '../../../../../routes';
import {
  HDYHAU_OPTIONS_WELLNESS,
  MY_VET_CLINIC,
} from '../../../../quotes/quoteFlow/constants/HDYHAUQuestions';
import { postIdentityHdyhau } from '../../../../quotes/quoteFlow/service/quoteFlowService';
import {
  formatWellnessQuotePayload,
  validateSAWQuoteForm,
} from '../wellness-quote-form-validation';
import WellnessQuoteTabCheckout from '../wellness-quote-tab-checkout';
import WellnessQuoteTabPlanSelection from '../wellness-quote-tab-plan-selection';
import WellnessQuoteTabUserPets from '../wellness-quote-tab-user-pets';
import WellnessQuoteToolbar from '../wellness-quote-toolbar';

function WellnessQuoteCreate() {
  const { punks2380AdminAddAPetFlowAddPaymentMethod } = useFlags();
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { state: routerState } = useLocation();
  const notify = useNotify();
  const stripe = useStripe();
  const elements = useElements();
  const currentStep = useGetCurrentStep();

  async function createStripeToken(stripe, elements) {
    if (!stripe) {
      notify('Stripe is not initialized', { type: 'warning' });
      throw new Error('Stripe is not initialized');
    }
    const cardElement = elements.getElement(CardNumberElement);
    const response = await stripe.createToken(cardElement);
    if (response.error) {
      const message =
        response.error.message || 'Please review the Payment Information';
      notify(`[SOMETHING WENT WRONG]: ${message}`, { type: 'error' });
      throw new Error(message);
    }
    return response.token.id;
  }

  async function finalizeWellness(data) {
    try {
      const stripeToken = await createStripeToken(stripe, elements);
      const { data: user } = await dataProvider.create(
        `${RoutePaths.wellnessQuote}/${data.id}/finalize`,
        {
          data: { ...data, stripe_token: stripeToken },
          returnJson: 'DATA',
        }
      );
      navigate(`/${RoutePaths.customers}/${user.id}/show`);
      return user;
    } catch (e) {
      const errorMsg = stripeErrorStatus[e?.message]?.error_description || e.message;
      notify(`Unable to complete transaction: ${errorMsg}`, { type: 'warning' });
      return Promise.reject(e);
    }
  }

  async function finalizeWellnessAAP(data) {
    try {
      if (punks2380AdminAddAPetFlowAddPaymentMethod && !user.payment_details) {
        const stripeToken = await createStripeToken(stripe, elements);
        const { data: userData } = await dataProvider.create(
          `${RoutePaths.wellnessQuote}/${quoteId}/finalize/add-a-pet`,
          {
            data: { ...data, stripe_token: stripeToken },
            returnJson: 'DATA',
          }
        );
        navigate(`/${RoutePaths.customers}/${userData.id}/show`);
        return userData;
      } else {
        const { data: userData } = await dataProvider.create(
          `${RoutePaths.wellnessQuote}/${quoteId}/finalize/add-a-pet`,
          {
            data,
            returnJson: 'DATA',
          }
        );
        navigate(`/${RoutePaths.customers}/${userData.id}/show`);
        return userData;
      }
    } catch (e) {
      const errorMsg = stripeErrorStatus[e?.message]?.error_description || e.message;
      notify(`Unable to complete transaction: ${errorMsg}`, { type: 'warning' });
      return Promise.reject(e);
    }
  }

  const {
    data: createdQuote,
    mutateAsync: createQuote,
    isLoading: isCreatingQuote,
  } = useMutation({
    mutationFn: data =>
      dataProvider
        .create(RoutePaths.wellnessQuote, { data })
        .then(response => response.data),
  });

  const {
    mutateAsync: editQuote,
    data: editedQuote,
    isLoading: isEditingQuote,
  } = useMutation({
    mutationFn: data =>
      dataProvider
        .update(RoutePaths.wellnessQuote, { id: createdQuote.id, data })
        .then(response => response.data),
  });

  const quote = editedQuote || createdQuote || {};
  const quoteId = quote?.id;

  const {
    isError: isEmailError,
    helperText: emailHelperText,
    user,
    isAddAPet,
  } = useCheckEmail();

  const [selectedPlans, setSelectedPlans] = useState({});

  // redirect to first page of flow if refreshing a create quote - do not do this for edit flow
  useEffect(() => {
    if (currentStep !== 0 && !quoteId) {
      navigate(`/${RoutePaths.wellnessQuote}/create`);
    }
  }, [quoteId, currentStep]);

  function save(formData, { onSuccess, onError } = {}) {
    const quoteAction = quoteId ? editQuote : createQuote;
    const finalizeAction = isAddAPet ? finalizeWellnessAAP : finalizeWellness;

    const action = currentStep === 2 ? finalizeAction : quoteAction;
    const payload = formatWellnessQuotePayload({
      formData,
      dbUser: isAddAPet ? user : {},
      quote,
      selectedPlans,
      currentStep,
    });

    if (!isAddAPet && currentStep === 2) {
      postIdentityHdyhau(quote.identity_id, {
        hdyhau: [
          {
            wellness_quote_id: quote.id,
            order: -1,
            option_identifier: formData.referral,
            option_value: HDYHAU_OPTIONS_WELLNESS.find(
              option => option.value === formData.referral
            ).label,
            vet_id:
              formData.referral === MY_VET_CLINIC ? formData.vet?.id : undefined,
          },
        ],
      });
    }

    return action(payload)
      .then(response => {
        onSuccess?.(response);
      })
      .catch(err => {
        onError?.(err);
      });
  }

  function validateForm(values) {
    return validateSAWQuoteForm(
      values,
      quote,
      isAddAPet ? user : {},
      currentStep,
      selectedPlans,
      {
        isEmailError,
        emailHelperText,
      }
    );
  }

  function handleEditClick() {
    navigate(`/${RoutePaths.wellnessQuote}/create`, { state: routerState });
  }

  /*
  issues with using same source on user-pets and on checkout, so duping data into
  two separate fields
   */
  const defaultValues = isAddAPet
    ? {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        billed_annually: user.is_charged_annually,
      }
    : {};

  return (
    <Create
      className='wellness-quote-create-root'
      title={<Title title='Create a Wellness Quote' />}
    >
      <SaveContextProvider
        value={{
          save,
          saving: isCreatingQuote || isEditingQuote,
          mutationMode: 'pessimistic',
        }}
      >
        <CustomTabbedForm
          toolbar={
            currentStep <= 1 ? (
              <WellnessQuoteToolbar edit={false} selectedPlans={selectedPlans} />
            ) : null
          }
          validate={validateForm}
          sx={{
            '& .MuiTabs-root': {
              display: 'none',
            },
          }}
          defaultValues={defaultValues}
        >
          <TabbedForm.Tab label='Details'>
            <WellnessQuoteTabUserPets disableAddRemovePets={!!quoteId} />
          </TabbedForm.Tab>

          <TabbedForm.Tab label='Plans'>
            <WellnessQuoteTabPlanSelection
              quote={quote}
              onSelectPlan={setSelectedPlans}
              onEditClick={handleEditClick}
            />
          </TabbedForm.Tab>

          <TabbedForm.Tab label='Checkout'>
            <WellnessQuoteTabCheckout quote={quote} selectedPlans={selectedPlans} />
          </TabbedForm.Tab>
        </CustomTabbedForm>
      </SaveContextProvider>
    </Create>
  );
}

export default WellnessQuoteCreate;
